import React, { memo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import times from 'lodash/times'
import AlertIcon from 'transfix-icons/build/AlertIcon'
import RightArrowIcon from 'transfix-icons/build/RightArrowIcon'
import Card from 'transfix-ui/components/Card'
import Skeleton from 'transfix-ui/core/Skeleton'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { brandColors } from 'transfix-ui'
import { HOMEPAGE_BOX_SHADOW, TEST_IDS } from '../../../constants'

const useShipmentsNeedingAttentionCardStyles = makeStyles(theme => ({
  card: {
    borderRadius: theme.spacing(0.5),
    boxShadow: HOMEPAGE_BOX_SHADOW,
    padding: theme.spacing(2),
    '&:hover': {
      backgroundColor: brandColors.coolGray1,
      cursor: 'pointer',
    },
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexGrow: 1,

    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(1),
    },
  },
  warningIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [theme.breakpoints.up('md')]: {
      borderRadius: theme.spacing(1),
      background: brandColors.warning0,
      padding: theme.spacing(1.5),

      '&.loading': {
        background: brandColors.coolGray1,
      },
    },

    '& svg': {
      width: theme.spacing(3),
      height: theme.spacing(3),

      [theme.breakpoints.up('md')]: {
        width: theme.spacing(2.5),
        height: theme.spacing(2.5),
      },
    },
  },
  titleWrapper: {
    flexGrow: 1,
  },
  titleSkeleton: {
    marginBottom: theme.spacing(0.5),
    height: 19,
    width: '100%',

    [theme.breakpoints.up('lg')]: {
      maxWidth: 400,
    },
  },
  itemizedCountSkeleton: {
    display: 'none',
    alignItems: 'center',
    gap: theme.spacing(1),

    [theme.breakpoints.up('lg')]: {
      display: 'flex',
    },
  },
  itemizedCount: {
    display: 'none',
    alignItems: 'center',
    gap: theme.spacing(3.75),

    [theme.breakpoints.up('lg')]: {
      display: 'flex',
    },
  },
  count: {
    position: 'relative',
    '&::after': {
      content: '""',
      display: 'block',
      width: theme.spacing(0.5),
      height: theme.spacing(0.5),
      background: brandColors.coolGray4,
      borderRadius: '50%',
      position: 'absolute',
      top: '50%',
      right: `-18px`,
      transform: 'translateY(-50%)',
    },
    '&:last-of-type::after': {
      display: 'none',
    },
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

export interface IShipmentsNeedingAttentionCardProps {
  totalCount: number
  missingAssignmentCount: number
  missingEtaCount: number
  missingDocsCount: number
  /**
   * @default false
   */
  loading?: boolean
}

const ShipmentsNeedingAttentionCard = memo<IShipmentsNeedingAttentionCardProps>(
  ({
    loading = false,
    missingAssignmentCount = 0,
    missingDocsCount = 0,
    missingEtaCount = 0,
    totalCount = 0,
  }) => {
    const { trackEvent } = useFMSAnalytics()
    const history = useHistory()
    const classes = useShipmentsNeedingAttentionCardStyles()

    const cardClickHandler = useCallback(() => {
      history.push('/loads-needing-attention')
      trackEvent('Homepage', 'HOME_NEEDS_ATTENTION_CLICKED_CARD')
    }, [history, trackEvent])

    return (
      <div onClick={cardClickHandler} role='button' tabIndex={0} aria-hidden='true'>
        <Card className={classes.card} dataTest={TEST_IDS.needsAttentionCard}>
          <div className={classes.cardContent}>
            <div className={classes.info}>
              <div className={`${classes.warningIcon} ${loading ? 'loading' : ''}`}>
                <AlertIcon color={loading ? 'coolGray5' : 'warning1'} size='large' />
              </div>
              <div className={classes.titleWrapper}>
                {loading ? (
                  <Skeleton variant='rectangular' className={classes.titleSkeleton} />
                ) : (
                  <Typography variant='subtitle1' component='div'>
                    {totalCount} Loads Need Attention
                  </Typography>
                )}
                {loading ? (
                  <div className={classes.itemizedCountSkeleton}>
                    {times(3).map(i => (
                      <Skeleton key={i} variant='rectangular' height={16} width={175} />
                    ))}
                  </div>
                ) : (
                  <div className={classes.itemizedCount}>
                    {missingAssignmentCount > 0 && (
                      <Typography
                        variant='body2'
                        color='textSecondary'
                        component='div'
                        className={classes.count}>
                        {missingAssignmentCount} need assignment details
                      </Typography>
                    )}
                    {missingEtaCount > 0 && (
                      <Typography
                        variant='body2'
                        color='textSecondary'
                        component='div'
                        className={classes.count}>
                        {missingEtaCount} need ETAs
                      </Typography>
                    )}
                    {missingDocsCount > 0 && (
                      <Typography
                        variant='body2'
                        color='textSecondary'
                        component='div'
                        className={classes.count}>
                        {missingDocsCount} need documents
                      </Typography>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={classes.iconWrapper}>
              <RightArrowIcon color='coolGray4' size='medium' />
            </div>
          </div>
        </Card>
      </div>
    )
  }
)

export default ShipmentsNeedingAttentionCard
